import React, { useState, useEffect } from 'react';
import { Search, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import axiosInstance from '../../lib/api/axiosInstance';
import { Contact } from '../../types';

interface ContactSelectorProps {
  selectedContactId?: number;
  onContactSelect: (contact: Contact | null) => void;
  placeholder?: string;
}

export const ContactSelector: React.FC<ContactSelectorProps> = ({
  selectedContactId,
  onContactSelect,
  placeholder = "Rechercher un contact..."
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  // Charger les contacts disponibles
  const loadContacts = async (search?: string) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get<Contact[]>('/users/available-contacts', {
        params: search ? { search } : {}
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les contacts au montage et quand la recherche change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadContacts(searchTerm);
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Charger le contact sélectionné si un ID est fourni
  useEffect(() => {
    if (selectedContactId) {
      const contact = contacts.find(c => c.id === selectedContactId);
      if (contact) {
        setSelectedContact(contact);
      }
    } else {
      setSelectedContact(null);
    }
  }, [selectedContactId, contacts]);

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
    onContactSelect(contact);
  };

  const handleClearSelection = () => {
    setSelectedContact(null);
    onContactSelect(null);
  };

  return (
    <div className="space-y-4">
      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Contact sélectionné */}
      {selectedContact && (
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{selectedContact.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedContact.email}</p>
                  {selectedContact.role && (
                    <Badge variant="secondary" className="mt-1">
                      {selectedContact.role}
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSelection}
              >
                Désélectionner
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des contacts disponibles */}
      {!selectedContact && (
        <Card>
          <CardContent className="p-0">
            <ScrollArea className="h-64">
              {loading ? (
                <div className="p-4 text-center text-muted-foreground">
                  Chargement des contacts...
                </div>
              ) : contacts.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  {searchTerm ? 'Aucun contact trouvé' : 'Aucun contact disponible'}
                </div>
              ) : (
                <div className="divide-y">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleContactSelect(contact)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <UserPlus className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{contact.name}</p>
                          <p className="text-sm text-muted-foreground truncate">{contact.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {contact.role && (
                              <Badge variant="outline" className="text-xs">
                                {contact.role}
                              </Badge>
                            )}
                            {contact.phone && (
                              <span className="text-xs text-muted-foreground">
                                {contact.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};